import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { ConfirmType } from '@/types/common.types';

interface ConfirmModalProps {
  title: string;
  message: string;
  confirmText: string;
  cancelText?: string;
  type?: ConfirmType;
  onConfirm: () => void;
  onClose: () => void;
}

const ICON_MAP: Record<ConfirmType, string> = {
  danger:  'fa-trash-alt',
  warning: 'fa-exclamation-triangle',
  info:    'fa-question-circle',
};

export function ConfirmModal({
  title,
  message,
  confirmText,
  cancelText,
  type = 'danger',
  onConfirm,
  onClose,
}: ConfirmModalProps) {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const messageId = useId();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // El foco entra a la tarjeta al abrir y regresa al disparador al cerrar — mismo patrón que
  // `components/common/Modal`. Sin esto el teclado/lector de pantalla se queda en el elemento que
  // abrió el modal, que en páginas largas (p. ej. la tabla de Campañas) puede quedar fuera del
  // viewport una vez que el modal se centra en pantalla.
  useEffect(() => {
    triggerRef.current = document.activeElement as HTMLElement | null;
    cardRef.current?.focus();
    return () => triggerRef.current?.focus();
  }, []);

  // `createPortal` a `document.body`: `ConfirmModal` se monta en pantallas de admin envueltas en
  // `.adm-section`/`.glass`, que llevan `backdrop-filter` — por especificación CSS eso convierte al
  // ancestro en el "containing block" de cualquier descendiente `position: fixed`. Sin portal,
  // `.confirm-modal-overlay` (`inset: 0`) queda encajado en el tamaño de esa sección en vez de
  // cubrir la ventana completa: en una tabla larga (p. ej. Campañas con muchas filas) el modal
  // termina centrado muy por debajo del scroll actual y el usuario no lo ve. Mismo fix ya aplicado
  // en `PostItDetailModal.tsx` para el mismo problema.
  return createPortal((
    <div className="confirm-modal-overlay">
      <div className="confirm-modal-backdrop" onClick={onClose} />
      <div
        ref={cardRef}
        className="confirm-modal-card"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={messageId}
        tabIndex={-1}
      >
        <div className={`confirm-modal-icon ${type}`}>
          <i className={`fas ${ICON_MAP[type]}`} aria-hidden="true" />
        </div>
        <h3 id={titleId} className="confirm-modal-title">{title}</h3>
        <p id={messageId} className="confirm-modal-message">{message}</p>
        <div className="confirm-modal-actions">
          <button className="btn-confirm-cancel" onClick={onClose}>
            {cancelText ?? t('common.cancel')}
          </button>
          <button
            className={`btn-confirm-accept ${type}`}
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
